import { useLicenses } from '../hooks';
import { formatarData } from '../utils/formatters';
import { Link } from 'react-router-dom';
import { licencaUrl } from '../utils/links';

interface Props {
  assetId: string;
  currentCustodianId: string | null;
  isSoftware: boolean;
}

/**
 * Aba "Licenças" do detalhe do ativo.
 * - Ativo software: lista licenças com asset_id = este ativo.
 * - Ativo físico: lista licenças cujos seats estão atribuídos ao mesmo
 *   custodian atual (informativo).
 *
 * Como a API não expõe filtro combinado desse tipo, usamos apenas o
 * caso software (mais frequente). Para físico, deixamos uma nota
 * apontando pra central de licenças. O backlog registra a melhoria.
 */
export function LicencasTab({ assetId, currentCustodianId, isSoftware }: Props) {
  const query = useLicenses({ limit: 100 });
  const items = query.data?.data ?? [];
  const related = items.filter((l) => l.asset?.id === assetId);

  if (query.isLoading) {
    return <p className="py-6 text-center text-sm text-gray-500">Carregando licenças...</p>;
  }
  if (query.isError) {
    return <p className="py-6 text-center text-sm text-red-600">Falha ao carregar licenças.</p>;
  }

  return (
    <div>
      {!isSoftware && (
        <p className="mb-4 text-xs text-gray-600">
          Ativo físico — a aba mostra licenças vinculadas explicitamente ao ativo.
          Para ver todas as licenças, filtre a{' '}
          <Link to={licencaUrl()} className="text-blue-600 hover:underline">listagem de ativos</Link> por software.
        </p>
      )}
      {related.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500">Nenhuma licença vinculada a este ativo.</p>
      ) : (
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Nome</th>
              <th className="px-3 py-2 text-left">Fornecedor</th>
              <th className="px-3 py-2 text-left">Uso</th>
              <th className="px-3 py-2 text-left">Vencimento</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {related.map((l) => (
              <tr key={l.id}>
                <td className="px-3 py-2 text-gray-800">
                  <Link to={licencaUrl(l.id)} className="text-blue-600 hover:underline">
                    {l.name}
                  </Link>
                </td>
                <td className="px-3 py-2 text-gray-700">{l.vendor}</td>
                <td className="px-3 py-2 text-gray-700">
                  {l.usedSeats}/{l.totalSeats}
                  {l.availableSeats <= 0 && <span className="ml-1 text-xs text-red-600">(limite atingido)</span>}
                </td>
                <td className="px-3 py-2 text-gray-700">
                  {l.expiresAt ? formatarData(l.expiresAt) : 'Perpétua'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* currentCustodianId reservado pra evolução futura (buscar seats do custodian) */}
      <p className="mt-4 text-[10px] text-gray-400">
        {currentCustodianId ? `Responsável atual: ${currentCustodianId.slice(0, 8)}...` : ''}
      </p>
    </div>
  );
}
